// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import loadMessages from "@insite/client-framework/Store/Data/Messages/Handlers/LoadMessages";
import updateMessageIsRead from "@insite/client-framework/Store/Data/Messages/Handlers/UpdateMessageIsRead";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { MessageModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Popover, { PopoverPresentationProps, PositionStyle } from "@insite/mobius/Popover";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Tooltip, { TooltipPresentationProps } from "@store/MobiusOverrides/Tooltip/Tooltip";
import Carousel, { CarouselPresentationProps, CarouselSlide } from "@store/Overrides/Components/Carousel";
import ChevronDown from "@store/Components/Toolkit/Icons/ChevronDown";
import EmailLine from "@store/Components/Toolkit/Icons/Email/EmailLine";
import AccountMessagesCount, {
    AccountMessagesCountPresentationProps,
} from "@store/Components/Toolkit/AccountMessagesCount";
import DataViewLoadingOverlay from "@store/Components/Toolkit/DataViewLoadingOverlay";
import usePersistentDataView from "@store/Framework/Common/Hooks/usePersistentDataView";
import { pxToRem, pxToRemArray } from "@store/Framework/Common/Utilities/unitHelper";
import { useMessagesDataView } from "@store/Framework/Store/Data/Messages/MessagesHook";
import { useSession } from "@store/Framework/Store/Context/ContextHooks";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { css } from "styled-components";
import { menuSecondaryNavigationItemStyles } from "@store/Themes/common/mixins/menu";
import { iconScalableStyles } from "@store/Themes/common/mixins/icon";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";
import breakpoint from "@store/Framework/Common/Utilities/breakpoint";
import { boxStyles } from "@store/Themes/common/mixins/box";
import ArrowForward from "@store/Components/Toolkit/Icons/ArrowForward";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { countBubblePositionStyles } from "@store/Themes/common/mixins/countBubble";
import InfoFill from "@store/Components/Toolkit/Icons/Info/InfoFill";
import { typographyGetNoResultsAltProps } from "@store/Themes/common/props/typography";
import EmailFill from "@store/Components/Toolkit/Icons/Email/EmailFill";

const enum fields {
    changeMessagesDestination = "changeMessagesDestination",
    messagesDestination = "messagesDestination",
    popoverPosition = "popoverPosition",
    numberOfMessagesToShowInCarousel = "numberOfMessagesToShowInCarousel",
}

interface HeaderMessagesProps extends WidgetProps {
    fields: {
        [fields.changeMessagesDestination]: boolean;
        [fields.messagesDestination]: LinkFieldValue;
        [fields.popoverPosition]: "left" | "right";
        [fields.numberOfMessagesToShowInCarousel]: number;
    };
}

export interface HeaderMessagesStyles {
    wrapper?: InjectableCss;
    popover?: PopoverPresentationProps;
    iconButton?: ButtonPresentationProps;
    iconWrapper?: InjectableCss;
    envelopeIcon?: IconPresentationProps;
    chevronIcon?: IconPresentationProps;
    popoverInner?: InjectableCss;
    headerBand?: InjectableCss;
    unreadCountWrapper?: InjectableCss;
    unreadCountText?: TypographyPresentationProps;
    unreadCountTooltip?: TooltipPresentationProps;
    accountMessagesCountStyles?: AccountMessagesCountPresentationProps;
    iconInButtonCmsStyles?: IconPresentationProps;
    viewAllLink?: LinkPresentationProps;
    emptyWrapper?: InjectableCss;
    emptyTextIcon?: IconPresentationProps;
    emptyText?: TypographyPresentationProps;
    carousel?: CarouselPresentationProps;
    slide?: InjectableCss;
    slideHeader?: InjectableCss;
    subjectText?: TypographyPresentationProps;
    dateText?: TypographyPresentationProps;
    bodyText?: TypographyPresentationProps;
    markAsReadLink?: LinkPresentationProps;
}

export const headerMessagesStyles: HeaderMessagesStyles = {
    wrapper: {
        css: css`
            ${menuSecondaryNavigationItemStyles()}
            position: relative;
            display: inline-flex;
            align-items: center;
            font-weight: ${x => x.theme.design.typography.weight.base};
        `,
    },
    popover: {
        contentBodyProps: {
            css: css`
                ${boxStyles(null)}
                width: ${pxToRem(550)};
                max-width: 100%;
                max-height: none;
                color: ${getColor("text")};
                overflow: visible;

                ${breakpoint("sm")} {
                    max-width: initial;
                }
            `,
        },
    },
    iconButton: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(4)};
            background: transparent;
            border: none;
            padding-left: 0;
            padding-right: 0;
            line-height: ${x => x.theme.design.typography.lineHeight.small};
            &:not(:focus-visible) {
                outline: none;
            }
        `,
    },
    iconWrapper: {
        css: css`
            width: ${pxToRem(24)};
            height: ${pxToRem(24)};
            position: relative;
            display: flex;
            align-items: center;
        `,
    },
    envelopeIcon: {
        src: EmailLine,
        css: css`
            ${iconScalableStyles(pxToRem(24), false)}
            color: ${getColor("menu.secondaryNavigation.contrast")};
        `,
    },
    chevronIcon: {
        src: ChevronDown,
        css: css`
            ${iconScalableStyles(pxToRem(14), false)}
            color: ${getColor("menu.secondaryNavigation.contrast")};
        `,
    },
    popoverInner: {
        css: css`
            min-width: ${pxToRem(360)};
        `,
    },
    headerBand: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: ${pxToRem(20)};
            border-bottom: 1px solid ${getColor("common.border")};
            background-color: ${getColor("common.accent")};
            border-top-left-radius: ${x => x.theme.design.common.contentRadius};
            border-top-right-radius: ${x => x.theme.design.common.contentRadius};
        `,
    },
    unreadCountWrapper: {
        css: css`
            display: inline-flex;
            align-items: flex-start;
            gap: ${pxToRem(6)};
        `,
    },
    unreadCountText: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
            border-bottom: 2px solid ${getColor("primary")};
            padding-bottom: ${pxToRem(2)};
        `,
    },
    unreadCountTooltip: {
        mergeCss: true,
        iconProps: {
            src: InfoFill,
        },
        cssOverrides: {
            tooltipContainer: css`
                bottom: ${pxToRem(-100)};
                width: ${pxToRem(280)};
            `,
            tooltipArrow: css`
                bottom: 100%;
                top: initial;
                border-color: transparent transparent ${getColor("common.backgroundContrast")} transparent;
            `,
        },
    },
    accountMessagesCountStyles: {
        position: pxToRemArray([-8, -10, null, null]),
    },
    iconInButtonCmsStyles: {
        src: InfoFill,
        css: css`
            ${countBubblePositionStyles(pxToRemArray([0, -10, null, null]))}
            ${iconScalableStyles(pxToRem(20), false)}
            position: absolute;
            color: ${getColor("secondary")};
        `,
    },
    viewAllLink: {
        css: css`
            ${linkActionStyles()}
            white-space: nowrap;
            font-weight: ${x => x.theme.design.typography.weight.bold};
            font-size: ${x => x.theme.design.typography.size.small};
        `,
        icon: {
            iconProps: {
                src: ArrowForward,
            },
            position: "right",
        },
    },
    emptyWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;
            min-height: ${pxToRem(150)};
            padding: ${pxToRem(20)};
        `,
    },
    emptyText: typographyGetNoResultsAltProps(),
    emptyTextIcon: {
        src: EmailFill,
        size: 32,
        css: css`
            ${spacingStyles(null, pxToRem(5))}
            color: ${getColor("primary")};
        `,
    },
    carousel: {
        loop: true,
        autoplay: false,
        arrowsGap: pxToRem(0),
        extendedStyles: {
            carouselSlide: {
                css: css`
                    display: flex;
                    flex-direction: column;
                `,
            },
            carouselControls: {
                css: css`
                    margin: ${pxToRem(15, 0)};
                `,
            },
        },
        itemsGap: pxToRem(30),
    },
    slide: {
        css: css`
            flex: 1;
            padding: ${pxToRem(20)};
            min-width: 0;
        `,
    },
    slideHeader: {
        css: css`
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: ${pxToRem(8)};
            margin-bottom: ${pxToRem(8)};
        `,
    },
    subjectText: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: ${pxToRem(400)};
        `,
    },
    dateText: {
        css: css`
            font-style: italic;
            font-size: ${x => x.theme.design.typography.size.small};
            white-space: nowrap;
            flex-shrink: 0;
            color: ${getColor("text.accent")};
        `,
    },
    bodyText: {
        css: css`
            margin-bottom: ${pxToRem(12)};
            overflow: hidden;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 4;
        `,
    },
    markAsReadLink: {
        css: css`
            ${linkActionStyles()}
            display: block;
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
};

const styles = headerMessagesStyles;

const messagesParameter = {};

const HeaderMessages: FC<HeaderMessagesProps> = ({
    fields: { changeMessagesDestination, messagesDestination, numberOfMessagesToShowInCarousel, popoverPosition },
}) => {
    const session = useSession();
    const { isAuthenticated, isGuest, language } = session;
    const isUserAuthenticated = isAuthenticated && !isGuest;
    const shouldShowMessages = isUserAuthenticated;
    const dispatch = useAppDispatch();
    const popoverRef = useRef<React.ComponentRef<typeof Popover>>(null);
    const popoverInsideRef = useRef<HTMLElement>(null);
    const { isInShell } = useShellContext();

    const destinationLink = useGetLink(messagesDestination);
    const fallbackUrl = useAppSelector(state => getPageLinkByPageType(state, "MyAccountPage")?.url);
    const destinationUrl = changeMessagesDestination ? destinationLink?.url : fallbackUrl;

    const [messagesCount, setMessagesCount] = useState(0);
    const baseMessagesDataView = useMessagesDataView(messagesParameter);
    const { value: messages, isLoading } = usePersistentDataView(baseMessagesDataView);
    const shouldLoadMessages = !isLoading && !messages;

    const positionFunction = useCallback(() => {
        const popoverStyles: PositionStyle = { position: "absolute" };
        popoverStyles[popoverPosition] = 0;
        return popoverStyles;
    }, [popoverPosition]);

    useEffect(() => {
        if (shouldLoadMessages && isUserAuthenticated) {
            dispatch(loadMessages(messagesParameter));
        }
    }, [shouldLoadMessages, isUserAuthenticated]);

    useEffect(() => {
        const _count = messages?.filter(msg => !msg.isRead).length || 0;
        setMessagesCount(_count);
    }, [messages]);

    const unreadMessages = useMemo(
        () =>
            !isUserAuthenticated
                ? []
                : (messages ?? [])
                      .filter((m: MessageModel) => !m.isRead)
                      .sort(
                          (a: MessageModel, b: MessageModel) =>
                              new Date(b.dateToDisplay).getTime() - new Date(a.dateToDisplay).getTime(),
                      )
                      .slice(0, numberOfMessagesToShowInCarousel),
        [messages, isUserAuthenticated, numberOfMessagesToShowInCarousel],
    );

    const handleMarkAsRead = (message: MessageModel) => {
        dispatch(updateMessageIsRead({ message, isRead: true }));
    };

    const slides: CarouselSlide[] = useMemo(
        () =>
            unreadMessages.map((message: MessageModel) => ({
                id: message.id,
                renderComponent: () => (
                    <StyledWrapper {...styles.slide}>
                        <StyledWrapper {...styles.slideHeader}>
                            <Typography {...styles.subjectText} title={message.subject}>
                                {message.subject}
                            </Typography>
                            <Typography {...styles.dateText}>
                                {getLocalizedDateTime({
                                    dateTime: message.dateToDisplay,
                                    language,
                                    options: { year: "numeric", month: "numeric", day: "numeric" },
                                })}
                            </Typography>
                        </StyledWrapper>
                        <Typography {...styles.bodyText}>{message.body}</Typography>
                        <Link
                            {...styles.markAsReadLink}
                            onClick={() => handleMarkAsRead(message)}
                            aria-label={translate("Mark as Read: {0}", message.subject)}
                        >
                            {translate("Mark as Read")}
                        </Link>
                    </StyledWrapper>
                ),
            })),
        [unreadMessages, language],
    );

    if (!shouldShowMessages && !isInShell) {
        return null;
    }

    const unreadCountLabel =
        messagesCount === 1
            ? translate("({0}) Unread message", messagesCount.toString())
            : translate("({0}) Unread messages", messagesCount.toString());

    const closePopover = () => {
        popoverRef.current?.closePopover(new Event("close"));
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Popover
                {...styles.popover}
                ref={popoverRef}
                wrapperProps={{
                    ...styles.popover?.wrapperProps,
                }}
                insideRefs={[popoverInsideRef]}
                contentBodyProps={{
                    ...styles.popover?.contentBodyProps,
                    as: "div",
                }}
                positionFunction={positionFunction}
                popoverTrigger={
                    <Button {...styles.iconButton} aria-haspopup="true">
                        <StyledWrapper {...styles.iconWrapper}>
                            <Icon {...styles.envelopeIcon} />
                            {!shouldShowMessages && isInShell ? (
                                <Icon {...styles.iconInButtonCmsStyles} />
                            ) : (
                                <AccountMessagesCount
                                    {...styles.accountMessagesCountStyles}
                                    overrideShowAccountMessagesNotificationCount
                                />
                            )}
                        </StyledWrapper>
                        <Icon {...styles.chevronIcon} />
                    </Button>
                }
            >
                <StyledWrapper {...styles.popoverInner}>
                    <StyledWrapper {...styles.headerBand}>
                        <StyledWrapper {...styles.unreadCountWrapper}>
                            <Typography {...styles.unreadCountText}>{unreadCountLabel}</Typography>
                            {messagesCount > numberOfMessagesToShowInCarousel && (
                                <Tooltip
                                    {...styles.unreadCountTooltip}
                                    text={siteMessage(
                                        "HeaderMessages_UnreadMessagesTooltip",
                                        numberOfMessagesToShowInCarousel.toString(),
                                    ).toString()}
                                    clickableTitle={translate("Open tooltip for more information")}
                                    triggerAltText={translate("Open tooltip for more information")}
                                />
                            )}
                        </StyledWrapper>
                        <Link {...styles.viewAllLink} href={destinationUrl} onClick={closePopover}>
                            {translate("Go To My Messages")}
                        </Link>
                    </StyledWrapper>
                    {!shouldShowMessages && isInShell ? (
                        <StyledWrapper {...styles.emptyWrapper}>
                            <Typography {...styles.emptyText}>
                                {translate("CMS note: This widget will only be visible when logged in.")}
                            </Typography>
                        </StyledWrapper>
                    ) : (
                        <DataViewLoadingOverlay dataView={baseMessagesDataView}>
                            {messagesCount === 0 ? (
                                <StyledWrapper {...styles.emptyWrapper}>
                                    <Icon {...styles.emptyTextIcon} />
                                    <Typography {...styles.emptyText}>{translate("No unread messages")}</Typography>
                                </StyledWrapper>
                            ) : (
                                <Carousel
                                    id="header-messages-carousel"
                                    {...styles.carousel}
                                    slides={slides}
                                    itemsPerSlide={1}
                                    showArrows={messagesCount > 1}
                                    showDots={messagesCount > 1}
                                    useSkipNav={false}
                                />
                            )}
                        </DataViewLoadingOverlay>
                    )}
                </StyledWrapper>
            </Popover>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: HeaderMessages,
    definition: {
        group: "Header",
        icon: "bell",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.popoverPosition,
                displayName: "Popover Position",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Right", value: "right" },
                    { displayName: "Left", value: "left" },
                ],
                defaultValue: "right",
                fieldType: "General",
                hideEmptyOption: true,
            },
            {
                name: fields.numberOfMessagesToShowInCarousel,
                displayName: "Number of Messages to Show in Carousel",
                editorTemplate: "IntegerField",
                defaultValue: 20,
                fieldType: "General",
                min: 3,
                max: 20,
            },
            {
                name: fields.changeMessagesDestination,
                displayName: "Change Messages Link Destination",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                variant: "toggle",
            },
            {
                name: fields.messagesDestination,
                displayName: "Messages Destination",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "Contextual",
                isRequired: true,
                allowUrls: () => false,
                allowCategories: () => false,
                isVisible: x => x.fields.changeMessagesDestination,
            },
        ],
    },
};

export default widgetModule;
